import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon, Pin } from 'lucide-react';
import { useNavigationPreferences } from '../hooks/useNavigationPreferences';

interface NavItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
  warningBadge?: number; // For overdue/critical items
  onClick?: () => void;
  collapsed?: boolean;
  isPinnable?: boolean;
}

export const NavItem: React.FC<NavItemProps> = ({
  to,
  icon: Icon,
  label,
  badge,
  warningBadge,
  onClick,
  collapsed = false,
  isPinnable = true,
}) => {
  const location = useLocation();
  const { preferences, pinItem } = useNavigationPreferences();
  const [isRippling, setIsRippling] = useState(false);
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0 });

  const isActive = 
    to === '/' 
      ? location.pathname === '/' 
      : location.pathname === to || location.pathname.startsWith(to + '/');

  const isPinned = preferences.pinnedItems.includes(to);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Create ripple effect
    const rect = e.currentTarget.getBoundingClientRect();
    setRipplePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsRippling(true);
    setTimeout(() => setIsRippling(false), 600);

    onClick?.();
  };

  const handlePin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    pinItem(to);
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`
        group relative flex items-center gap-3 px-3 py-2.5 rounded-lg
        transition-all duration-200 ease-out
        ${isActive 
          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        }
        ${collapsed ? 'justify-center' : ''}
        overflow-hidden
      `}
    >
      {/* Ripple effect */}
      {isRippling && (
        <span
          className="absolute rounded-full bg-primary-400/30 dark:bg-primary-600/30 animate-ping"
          style={{
            left: ripplePosition.x,
            top: ripplePosition.y,
            width: 10,
            height: 10,
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}

      {/* Active indicator */}
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary-600 dark:bg-primary-400 rounded-r-full" />
      )}

      {/* Icon */}
      <Icon 
        size={20} 
        className={`
          flex-shrink-0 transition-transform duration-200
          ${isActive ? 'scale-110' : 'group-hover:scale-105'}
        `}
      />

      {/* Label */}
      {!collapsed && (
        <span className="flex-1 font-medium text-sm truncate">
          {label}
        </span>
      )}

      {/* Badge */}
      {!collapsed && badge !== undefined && badge > 0 && (
        <div className="flex items-center gap-1">
          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-primary-600 dark:bg-primary-500 rounded-full">
            {badge > 99 ? '99+' : badge}
          </span>
          {warningBadge !== undefined && warningBadge > 0 && (
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-error dark:bg-error rounded-full">
              {warningBadge > 99 ? '99+' : warningBadge}
            </span>
          )}
        </div>
      )}

      {/* Warning badge only (when no regular badge) */}
      {!collapsed && badge === undefined && warningBadge !== undefined && warningBadge > 0 && (
        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-error dark:bg-error rounded-full">
          {warningBadge > 99 ? '99+' : warningBadge}
        </span>
      )}

      {/* Pin button */}
      {!collapsed && isPinnable && (
        <button
          onClick={handlePin}
          className={`
            opacity-0 group-hover:opacity-100 transition-opacity duration-200
            p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700
            ${isPinned ? 'opacity-100 text-primary-600 dark:text-primary-400' : ''}
          `}
          title={isPinned ? 'Unpin' : 'Pin'}
        >
          <Pin size={14} className={isPinned ? 'fill-current' : ''} />
        </button>
      )}

      {/* Tooltip for collapsed state */}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          {label}
          {badge !== undefined && badge > 0 && (
            <span className="ml-2 inline-flex items-center gap-1">
              <span className="text-primary-400">({badge})</span>
              {warningBadge !== undefined && warningBadge > 0 && (
                <span className="text-error">({warningBadge})</span>
              )}
            </span>
          )}
          {badge === undefined && warningBadge !== undefined && warningBadge > 0 && (
            <span className="ml-2 text-error">({warningBadge})</span>
          )}
        </div>
      )}
    </Link>
  );
};
